import type {FarmState} from './model';
import {defaultState} from './defaults';
import {createDomain} from './farm';
import {addDays,uid,validDate} from './dates';

function object(value: unknown, field: string): Record<string,unknown> {
  if (!value || typeof value!=='object' || Array.isArray(value)) throw new Error('Campo inválido: '+field);
  return value as Record<string,unknown>;
}
function records(value: unknown, field: string): Record<string,unknown>[] {
  if (!Array.isArray(value)) throw new Error('Campo inválido: '+field);
  return value.map((record,index)=>object(record,`${field}[${index}]`));
}
const numberFields=new Set('numPartos partosPrevios hembras machos nacidosVivos nacidosMuertos momias pesoCamada pezonesFuncionales cantidadInicial cantidadActual pesoPromedio diaEdadAlCrear cantidad pesoEntrada pesoSalida libras librasCerda librasLechones pesoTotal dia orden lote cerdaAuto'.split(' '));
const booleanFields=new Set('primeriza enObservacion imprimirPendiente gestacionConfirmada infeccion intervencionManual activo sinRegistroParto nacimientoDiaCero'.split(' '));
const textFields=new Set('id arete raza origen lugarCompra estado observaciones motivoObservacion nombre tipoServicio verracoId verracoExterno proveedor metodoConfirmacion observacionGestacion partoId loteId espacioId tipo espacioActual camadaId faseId tipoRef ref evento etapa causa origenCamadaId destinoCamadaId titulo categoria detalle destinatarioTipo destinatarioId destinatarioNombre aplicaA acciones title detail'.split(' '));
const dateFields=new Set('fechaNacimiento fechaEntrada fechaInicioReemplazo fecha fechaConfirmacion fechaNacimientoReferencia fechaCreacion fechaSalidaFinca fechaSalida desde hasta fechaDesde fechaCambioFicha'.split(' '));
function validateFields(record: Record<string,unknown>, field: string) {
  for (const [key,value] of Object.entries(record)) {
    if (value===null || value===undefined) continue; // optional values in historical backups
    if (numberFields.has(key) && (typeof value!=='number'||!Number.isFinite(value)||value<0)) throw new Error('Número inválido: '+field+'.'+key);
    if (booleanFields.has(key) && typeof value!=='boolean') throw new Error('Valor inválido: '+field+'.'+key);
    if (textFields.has(key) && typeof value!=='string') throw new Error('Texto inválido: '+field+'.'+key);
    if (dateFields.has(key) && (typeof value!=='string'||(value!==''&&!validDate(value)))) throw new Error('Fecha inválida: '+field+'.'+key);
  }
}
function requiredText(record: Record<string,unknown>, keys: string[], field: string) {
  for(const key of keys)if(typeof record[key]!=='string' || !record[key])throw new Error('Texto obligatorio inválido: '+field+'.'+key);
}
function validateReference(value: unknown, field: string) {
  const ref=object(value,field);requiredText(ref,['type','id'],field);
}

export function normalizeState(input: unknown): FarmState {
  if (!input || typeof input!=='object' || Array.isArray(input)) throw new Error('La copia no contiene un estado válido');
  const raw=structuredClone(input) as Record<string,unknown>;
  if (!Array.isArray(raw.cerdas) || !Array.isArray(raw.lotes)) throw new Error('La copia no es de Xtreme Pork');
  const defaults=defaultState();
  for (const [key,fallback] of Object.entries(defaults)) {
    if (raw[key]===undefined) raw[key]=fallback;
    else if (Array.isArray(fallback) && !Array.isArray(raw[key])) throw new Error('Campo inválido: '+key);
  }
  for (const [key,fallback] of Object.entries(defaults)) {
    if (Array.isArray(fallback) && key!=='tareasHechas') {
      for (const record of records(raw[key],key)) validateFields(record,key);
    }
  }
  if (!(raw.tareasHechas as unknown[]).every(key=>typeof key==='string')) throw new Error('Lista de tareas terminadas inválida');
  // Optional additions: do not rewrite older backups merely by opening them.
  if(raw.aplicacionesProcedimientos!==undefined)for(const record of records(raw.aplicacionesProcedimientos,'aplicacionesProcedimientos')) {
    validateFields({...record,ref:undefined},'aplicacionesProcedimientos');
    requiredText(record,['id','procedimientoId','fecha','titulo','subject','area'],'aplicacionesProcedimientos');
    if(!validDate(record.fecha as string))throw new Error('Fecha de aplicación inválida');
    if(typeof record.acciones!=='string' || typeof record.detalle!=='string')throw new Error('Detalle de aplicación inválido');
    validateReference(record.ref,'aplicacionesProcedimientos.ref');
  }
  if(raw.tareasRealizadas!==undefined)for(const record of records(raw.tareasRealizadas,'tareasRealizadas')) {
    requiredText(record,['doneKey','area'],'tareasRealizadas');
    if(record.id!==undefined)requiredText(record,['id'],'tareasRealizadas');
    if(record.legacy!==undefined && typeof record.legacy!=='boolean')throw new Error('Origen de tarea realizada inválido');
    if(record.archived!==undefined && typeof record.archived!=='boolean')throw new Error('Archivo de tarea realizada inválido');
    if(record.legacy!==true)requiredText(record,['completedAt','fechaRealizacion'],'tareasRealizadas');
    if(record.fechaRealizacion!==undefined && (typeof record.fechaRealizacion!=='string' || !validDate(record.fechaRealizacion)))throw new Error('Fecha de realización inválida');
    if(record.completedAt!==undefined && (typeof record.completedAt!=='string' || !/^\d{4}-\d{2}-\d{2}T/.test(record.completedAt) || !Number.isFinite(Date.parse(record.completedAt))))throw new Error('Instante de realización inválido');
    const event=object(record.event,'tareasRealizadas.event');
    validateFields({...event,ref:undefined},'tareasRealizadas.event');
    requiredText(event,['doneKey','tipo','label'],'tareasRealizadas.event');
    if(event.doneKey!==record.doneKey)throw new Error('La clave de la tarea realizada no coincide');
    if(typeof event.date!=='string' || (event.date!=='' && !validDate(event.date)))throw new Error('Fecha programada inválida');
    if(typeof event.prioridad!=='number' || !Number.isFinite(event.prioridad))throw new Error('Prioridad de tarea inválida');
    for(const key of ['action','detail','subject','protocolGroup','referenceLabel','camadaId','manualId']) {
      if(event[key]!==undefined && typeof event[key]!=='string')throw new Error('Texto de tarea realizada inválido: '+key);
    }
    if(event.protocolDay!==undefined && (typeof event.protocolDay!=='number' || !Number.isFinite(event.protocolDay) || event.protocolDay<0))throw new Error('Día de protocolo inválido');
    if(event.fechaOriginal!==undefined && (typeof event.fechaOriginal!=='string' || (event.fechaOriginal!=='' && !validDate(event.fechaOriginal))))throw new Error('Fecha original de tarea inválida');
    if(event.ref!==undefined)validateReference(event.ref,'tareasRealizadas.event.ref');
  }
  const counters=object(raw.counters,'counters');
  for (const [key,fallback] of Object.entries(defaults.counters)) counters[key]??=fallback;
  validateFields(counters,'counters');
  object(raw.configuracion,'configuracion');
  const overrides=object(raw.eventOverrides,'eventOverrides');
  for (const [key,value] of Object.entries(overrides)) {
    const entry=object(value,'eventOverrides.'+key);validateFields(entry,'eventOverrides.'+key);
    if(entry.date!==undefined&&(typeof entry.date!=='string'||(entry.date!==''&&!validDate(entry.date)))) throw new Error('Fecha reprogramada inválida');
  }
  const protocols=object(raw.protocolos,'protocolos');
  for (const [key,fallback] of Object.entries(defaults.protocolos)) {
    if(protocols[key]===undefined)protocols[key]=fallback;
    for(const record of records(protocols[key],'protocolos.'+key))validateFields(record,'protocolos.'+key);
  }
  for(const record of records(raw.lotes,'lotes')) {
    for(const key of ['historial','incorporaciones']) {
      if(record[key]===undefined)record[key]=[];
      for(const entry of records(record[key],'lotes.'+key))validateFields(entry,'lotes.'+key);
    }
    record.camadaIds??=[];
    if(!Array.isArray(record.camadaIds)||!record.camadaIds.every(value=>typeof value==='string'))throw new Error('Camadas del lote inválidas');
    if(record.origenManual!=null)validateFields(object(record.origenManual,'origenManual'),'origenManual');
  }
  for(const record of records(raw.montas,'montas')) if(record.verracoIds!==undefined&&(!Array.isArray(record.verracoIds)||!record.verracoIds.every(id=>typeof id==='string')))throw new Error('Verracos de monta inválidos');
  for(const record of records(raw.camadas,'camadas'))if(record.muertes!==undefined)records(record.muertes,'camadas.muertes');
  const state=raw as unknown as FarmState;
  for (const [key,items] of Object.entries(defaults.protocolos)) {
    if(!state.protocolos || typeof state.protocolos!=='object') state.protocolos=defaults.protocolos;
    const section=key as keyof FarmState['protocolos'];
    if(!Array.isArray(state.protocolos[section])) state.protocolos[section]=items;
  }
  for (const key of ['cerdas','verracos','montas','celos','repeticiones','partos','camadas','lotes','espacios','espaciosCerdas','alimentacion','racionesLactancia','pesajes','mortalidad','tareasManual'] as const) {
    for (const record of state[key]) if(!record || typeof record!=='object' || Array.isArray(record)) throw new Error('Registro inválido: '+key);
  }
  state.lotes.forEach(lote=>{lote.historial??=[];lote.camadaIds??=[];});
  const {syncCerdaReproState,getCerda,normalizarOrdenEspaciosCerdas}=createDomain(state);
  function ensureStateShape(){
  if(!state.protocolos||typeof state.protocolos!=='object') state.protocolos=defaultState().protocolos;
  if(!Array.isArray(state.protocolos.reemplazoCerda)) state.protocolos.reemplazoCerda=[];
  if(!Array.isArray(state.protocolos.reemplazoVerraco)) state.protocolos.reemplazoVerraco=[];
  if(!state.eventOverrides||typeof state.eventOverrides!=='object'||Array.isArray(state.eventOverrides)) state.eventOverrides={};
  if(!Array.isArray(state.espaciosCerdas)) state.espaciosCerdas=[];
  if(!Array.isArray(state.transferenciasLechones)) state.transferenciasLechones=[];
  if(!Array.isArray(state.tareasManual)) state.tareasManual=[];
  if(!Array.isArray(state.celos)) state.celos=[];
  if(!Array.isArray(state.racionesLactancia)) state.racionesLactancia=[];
  if(!Array.isArray(state.procedimientos)) state.procedimientos=[];
  state.montas.forEach(monta=>{
    const ids=Array.isArray(monta.verracoIds)?monta.verracoIds.filter(Boolean):[];
    if(monta.verracoId&&!ids.includes(monta.verracoId)) ids.unshift(monta.verracoId);
    monta.verracoIds=[...new Set(ids)].slice(0,2);
    monta.verracoId=monta.verracoIds[0]||'';
  });
  state.cerdas.forEach(cerda=>{
    const registrados=state.partos.filter(p=>p.arete===cerda.arete).length;
    if(!Number.isInteger(cerda.partosPrevios)) cerda.partosPrevios=Math.max(0,(+cerda.numPartos||0)-registrados);
    syncCerdaReproState(cerda.arete);
  });
  if(!state.configuracion||typeof state.configuracion!=='object') state.configuracion={};
  const migrarNacimientoDiaCero=state.configuracion.nacimientoDiaCero!==true;
  state.lotes.forEach(lote=>{
    if(!Number.isInteger(lote.diaEdadAlCrear)||lote.diaEdadAlCrear<0) lote.diaEdadAlCrear=1;
    if(!Array.isArray(lote.incorporaciones)) lote.incorporaciones=[];
    if(migrarNacimientoDiaCero&&lote.fechaNacimientoReferencia) lote.fechaNacimientoReferencia=addDays(lote.fechaNacimientoReferencia,-1);
    if(!lote.fechaNacimientoReferencia) lote.fechaNacimientoReferencia=addDays(lote.fechaCreacion,-lote.diaEdadAlCrear);
  });
  state.configuracion.nacimientoDiaCero=true;
  const cerdasUbicadas=new Set();
  state.espaciosCerdas.forEach((espacio,index)=>{
    if(!espacio.id) espacio.id=uid('espcerda');
    if(!espacio.nombre) espacio.nombre=`${espacio.tipo==='paridera'?'P':'G'}-${index+1}`;
    if(!['gestacion','paridera'].includes(espacio.tipo)) espacio.tipo='gestacion';
    if(!Number.isInteger(+espacio.orden)||+espacio.orden<1) espacio.orden=index+1;
    if(!getCerda(espacio.cerdaArete)||cerdasUbicadas.has(espacio.cerdaArete)) espacio.cerdaArete=null;
    if(espacio.cerdaArete) cerdasUbicadas.add(espacio.cerdaArete);
  });
  normalizarOrdenEspaciosCerdas();
}
  ensureStateShape();
  return state;
}
export function hasOperationalData(candidate: unknown): boolean {
  if (!candidate || typeof candidate!=='object') return false;
  return Object.entries(candidate).some(([key,value])=>Array.isArray(value) && !['tareasHechas'].includes(key) && value.length>0);
}
